import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import classnames from 'classnames';
import React__default from 'react';
import useCanvasId from '../utils/useCanvasId.js';
import InputCheckbox from './Input/Checkbox.js';
import InputCheckboxes from './Input/Checkboxes.js';
import InputChip from './Input/Chip.js';
import InputChips from './Input/Chips.js';
import InputColor from './Input/Color.js';
import InputCurrency from './Input/Currency.js';
import InputDate from './Input/Date.js';
import InputDatetimeLocal from './Input/DatetimeLocal.js';
import { InputDropArea } from './Input/DropArea.js';
import InputDropWindow from './Input/DropWindow.js';
import InputEmail from './Input/Email.js';
import InputFeedback from './Input/Feedback.js';
import InputIconToggle from './Input/IconToggle.js';
import InputInfo from './Input/Info.js';
import InputLabel from './Input/Label.js';
import InputMonth from './Input/Month.js';
import InputNumber from './Input/Number.js';
import InputPassword from './Input/Password.js';
import InputRadio from './Input/Radio.js';
import InputRange from './Input/Range.js';
import InputRating from './Input/Rating.js';
import InputSelect from './Input/Select.js';
import InputTelephone from './Input/Telephone.js';
import InputText from './Input/Text.js';
import FormTextarea from './Input/Textarea.js';
import InputTime from './Input/Time.js';
import InputToggle from './Input/Toggle.js';
import InputUrl from './Input/Url.js';
import InputWeek from './Input/Week.js';

var _excluded = ["as", "className", "containerProps", "disabled", "disableFieldInfo", "error", "errorText", "id", "highlight", "hintText", "label", "labelProps", "size", "type", "required", "rightContent"];

// FIELD (takes the above components and generates a formfield and label component)
var FormField = function FormField(_ref, ref) {
  var _ref$as = _ref.as,
    As = _ref$as === void 0 ? 'div' : _ref$as,
    className = _ref.className,
    containerProps = _ref.containerProps,
    disabled = _ref.disabled,
    disableFieldInfo = _ref.disableFieldInfo,
    error = _ref.error,
    errorText = _ref.errorText,
    idFromProps = _ref.id,
    highlight = _ref.highlight,
    hintText = _ref.hintText,
    label = _ref.label,
    labelProps = _ref.labelProps,
    size = _ref.size,
    _ref$type = _ref.type,
    type = _ref$type === void 0 ? 'text' : _ref$type,
    _ref$required = _ref.required,
    required = _ref$required === void 0 ? false : _ref$required,
    rightContent = _ref.rightContent,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var finalClasses = classnames('C-FormField', className);
  var id = useCanvasId(idFromProps, 'formfield');
  var errorHintText = function errorHintText() {
    return (error || hintText) && !disableFieldInfo && /*#__PURE__*/React__default.createElement(InputInfo, {
      disabled: error ? false : true,
      error: error
    }, error ? errorText : hintText);
  };
  var FieldInput;
  switch (type) {
    case 'checkbox':
      return /*#__PURE__*/React__default.createElement(As, _extends({
        className: finalClasses
      }, containerProps), /*#__PURE__*/React__default.createElement(InputCheckbox, _extends({
        disabled: disabled,
        highlight: highlight,
        id: id,
        label: label,
        error: error,
        required: required,
        "aria-placeholder": error ? null : hintText,
        "aria-errormessage": errorText
      }, otherProps)), errorHintText());
    case 'checkboxes':
      FieldInput = InputCheckboxes;
      return /*#__PURE__*/React__default.createElement(As, _extends({
        className: finalClasses
      }, containerProps), label && /*#__PURE__*/React__default.createElement(InputLabel, _extends({
        disabled: disabled,
        htmlFor: id,
        error: error,
        required: required,
        rightContent: rightContent
      }, labelProps), label), /*#__PURE__*/React__default.createElement(InputCheckboxes, _extends({
        disabled: disabled,
        highlight: highlight,
        type: type,
        id: id,
        required: required,
        error: error,
        "aria-placeholder": error ? null : hintText,
        "aria-errormessage": errorText
      }, otherProps, {
        ref: ref
      })), errorHintText());
    case 'chip':
      return /*#__PURE__*/React__default.createElement(As, _extends({
        className: finalClasses
      }, containerProps), /*#__PURE__*/React__default.createElement(InputChip, _extends({
        disabled: disabled,
        highlight: highlight,
        id: id,
        label: label,
        size: size
      }, otherProps)));
    case 'chips':
      FieldInput = InputChips;
      break;
    case 'color':
      FieldInput = InputColor;
      break;
    case 'currency':
      FieldInput = InputCurrency;
      return /*#__PURE__*/React__default.createElement(As, _extends({
        className: finalClasses
      }, containerProps), /*#__PURE__*/React__default.createElement(InputLabel, _extends({
        "aria-label": label ? label : 'Currency Field',
        disabled: disabled,
        htmlFor: id,
        error: error,
        required: required,
        rightContent: rightContent
      }, labelProps), label), /*#__PURE__*/React__default.createElement(InputCurrency, _extends({
        className: className,
        disabled: disabled,
        highlight: highlight,
        id: id,
        required: required,
        error: error
      }, otherProps)), errorHintText());
    case 'date':
      FieldInput = InputDate;
      break;
    case 'datetime-local':
      FieldInput = InputDatetimeLocal;
      break;
    case 'drop-area':
      return /*#__PURE__*/React__default.createElement(As, _extends({
        className: finalClasses
      }, containerProps), /*#__PURE__*/React__default.createElement(InputDropArea, _extends({
        disabled: disabled,
        id: id,
        size: size,
        error: error,
        required: required
      }, otherProps)));
    case 'drop-window':
      return /*#__PURE__*/React__default.createElement(As, _extends({
        className: finalClasses
      }, containerProps), /*#__PURE__*/React__default.createElement(InputDropWindow, _extends({
        disabled: disabled,
        id: id,
        error: error,
        required: required
      }, otherProps)));
    case 'email':
      FieldInput = InputEmail;
      break;
    case 'feedback':
      FieldInput = InputFeedback;
      break;
    case 'icon-toggle':
      return /*#__PURE__*/React__default.createElement(As, _extends({
        className: finalClasses
      }, containerProps), /*#__PURE__*/React__default.createElement(InputIconToggle, _extends({
        disabled: disabled,
        highlight: highlight,
        size: size,
        id: id,
        error: error
      }, otherProps)));
    case 'month':
      FieldInput = InputMonth;
      break;
    case 'number':
      FieldInput = InputNumber;
      break;
    case 'password':
      FieldInput = InputPassword;
      break;
    case 'radio':
      FieldInput = InputRadio;
      break;
    case 'range':
      FieldInput = InputRange;
      break;
    case 'rating':
      return /*#__PURE__*/React__default.createElement(As, _extends({
        className: finalClasses
      }, containerProps), label && /*#__PURE__*/React__default.createElement(InputLabel, _extends({
        disabled: disabled,
        htmlFor: id,
        error: error,
        required: required,
        rightContent: rightContent
      }, labelProps), label), /*#__PURE__*/React__default.createElement(InputRating, _extends({
        disabled: disabled,
        highlight: highlight,
        size: size,
        id: id,
        error: error
      }, otherProps)), errorHintText());
    case 'select':
      FieldInput = InputSelect;
      break;
    case 'telephone':
      FieldInput = InputTelephone;
      break;
    case 'text':
      FieldInput = InputText;
      break;
    case 'textarea':
      FieldInput = FormTextarea;
      break;
    case 'toggle':
      return /*#__PURE__*/React__default.createElement(As, _extends({
        className: finalClasses
      }, containerProps), /*#__PURE__*/React__default.createElement(InputToggle, _extends({
        disabled: disabled,
        highlight: highlight,
        id: id,
        error: error,
        label: label
      }, otherProps)));
    case 'time':
      FieldInput = InputTime;
      break;
    case 'url':
      FieldInput = InputUrl;
      break;
    case 'week':
      FieldInput = InputWeek;
      break;
    default:
      console.error("Hey Canvas User! The Form.Field does not support the input type \"".concat(type, "\". Please check the Canvas docs site for supported input types. Contact the Enterprise UI team if you need the type \"").concat(type, "\" added."));
      return /*#__PURE__*/React__default.createElement(As, _extends({
        className: finalClasses
      }, containerProps), label && /*#__PURE__*/React__default.createElement(InputLabel, _extends({
        error: error,
        disabled: disabled,
        htmlFor: id,
        required: required,
        rightContent: rightContent
      }, labelProps), label), /*#__PURE__*/React__default.createElement("input", _extends({
        disabled: disabled,
        highlight: highlight,
        type: type,
        id: id
      }, otherProps, {
        required: required,
        "aria-placeholder": error ? null : hintText,
        "aria-errormessage": errorText
      }, otherProps)), !disableFieldInfo && /*#__PURE__*/React__default.createElement(InputInfo, {
        disabled: disabled,
        error: error
      }, error ? errorText : hintText));
  }
  return /*#__PURE__*/React__default.createElement(As, _extends({
    className: finalClasses
  }, containerProps), label && /*#__PURE__*/React__default.createElement(InputLabel, _extends({
    error: error,
    disabled: disabled,
    htmlFor: id,
    required: required,
    rightContent: rightContent
  }, labelProps), label), /*#__PURE__*/React__default.createElement(FieldInput, _extends({
    error: error,
    disabled: disabled,
    highlight: highlight,
    type: type,
    id: id,
    size: size
  }, otherProps, {
    ref: ref,
    required: required,
    "aria-placeholder": error ? null : hintText,
    "aria-errormessage": errorText
  })), !disableFieldInfo && /*#__PURE__*/React__default.createElement(InputInfo, {
    disabled: disabled,
    error: error
  }, error ? errorText : hintText));
};
FormField.displayName = 'Form.Field';
var FormField$1 = /*#__PURE__*/React__default.forwardRef(FormField);

export { FormField$1 as default };
